import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getMisl, setMisl, clearMisl, type StoredMislMember } from './mislStore';
import { ChevronLeft, ArrowRight, MapPin, Plus, Copy, Check, MessageCircle, Share2, UserPlus, Crown, User, Sparkles, Trophy, Flame, Swords, Zap, PartyPopper } from 'lucide-react';
const CornerOrnaments = () => <>
    <span className="absolute top-0 left-0 w-2.5 h-2.5 border-t border-l border-[#D4AF37]/50 pointer-events-none" data-id="element-2014" data-mp="33wnox4nprwcr" />
    <span className="absolute top-0 right-0 w-2.5 h-2.5 border-t border-r border-[#D4AF37]/50 pointer-events-none" data-id="element-2015" data-mp="38tnxbm81db71" />
    <span className="absolute bottom-0 left-0 w-2.5 h-2.5 border-b border-l border-[#D4AF37]/50 pointer-events-none" data-id="element-2016" data-mp="2uwsdcyq8xzc7" />
    <span className="absolute bottom-0 right-0 w-2.5 h-2.5 border-b border-r border-[#D4AF37]/50 pointer-events-none" data-id="element-2017" data-mp="1jfiv2fiutdch" />
  </>;
type Member = {
  id: string;
  name: string;
  handle: string;
  role: 'founder' | 'member';
  joinedAt: string;
};
export const CreateMislPanel = ({
  embedded = true,
  onClose
}: {
  embedded?: boolean;
  onClose?: () => void;
}) => {
  const [created, setCreated] = useState(false);
  const [justCreated, setJustCreated] = useState(false);
  const [copied, setCopied] = useState(false);
  const [name, setName] = useState('');
  const [region, setRegion] = useState('');
  const [members, setMembers] = useState<Member[]>([]);
  const [simulating, setSimulating] = useState(false);
  const target = 5;
  const memberCount = members.length;
  useEffect(() => {
    const existing = getMisl();
    if (existing) {
      setName(existing.name);
      setRegion(existing.region);
      setMembers(existing.members as Member[]);
      setCreated(true);
    }
  }, []);
  useEffect(() => {
    if (!created) return;
    setMisl({
      name,
      region,
      members: members as StoredMislMember[],
      target,
      createdAt: Date.now()
    });
  }, [created, name, region, members]);
  const progress = Math.min(100, Math.round(memberCount / target * 100));
  const isActive = memberCount >= target;
  const inviteLink = `lahoretreasury.com/misl/${(name || 'your-misl').toLowerCase().replace(/\s+/g, '-')}`;
  const handleCreate = () => {
    if (!name.trim()) return;
    setMembers([{
      id: 'me',
      name: 'Harnoor S.',
      handle: '@harnoor_s',
      role: 'founder',
      joinedAt: 'just now'
    }]);
    setCreated(true);
    setJustCreated(true);
  };
  useEffect(() => {
    if (justCreated) {
      const t = setTimeout(() => setJustCreated(false), 2200);
      return () => clearTimeout(t);
    }
  }, [justCreated]);
  const handleCopy = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  const handleWhatsApp = () => {
    const msg = encodeURIComponent(`🛡️ Join my Misl on Lahore Treasury — ${name || 'a new house'}.\nWe need ${target - memberCount} more to activate our house in the Court.\n\n${inviteLink}`);
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };
  const handleSimulateJoin = () => {
    const samplePeople = [{
      name: 'Arjan S.',
      handle: '@arjan_s'
    }, {
      name: 'Simran K.',
      handle: '@simran_k'
    }, {
      name: 'Harjeet K.',
      handle: '@harjeet_k'
    }, {
      name: 'Dilraj S.',
      handle: '@dilraj_s'
    }];
    const next = samplePeople[memberCount - 1];
    if (!next) return;
    setSimulating(true);
    setTimeout(() => {
      setMembers(prev => [...prev, {
        id: `m${prev.length}`,
        name: next.name,
        handle: next.handle,
        role: 'member',
        joinedAt: 'just now'
      }]);
      setSimulating(false);
    }, 400);
  };
  // ============ Stage 1: Pre-creation ============
  const steps = [{
    n: '01',
    icon: Crown,
    title: 'Name Your House',
    body: 'Pick a name your clan will rally around — your Misl is yours to lead.'
  }, {
    n: '02',
    icon: Share2,
    title: 'Invite Your People',
    body: 'Send your private invite link on WhatsApp. Everyone you bring in can invite more.'
  }, {
    n: '03',
    icon: Flame,
    title: 'Activate at 5 Members',
    body: 'Reach 5 members and your house goes live — earning XP, ranking, and Court standing together.'
  }];
  const perks = [{
    icon: Trophy,
    label: 'Misl Leaderboard'
  }, {
    icon: Swords,
    label: 'Group Rewards'
  }, {
    icon: Zap,
    label: 'Founder Crown'
  }, {
    icon: Sparkles,
    label: 'Custom Crest'
  }];
  return <div className={embedded ? '' : 'max-w-3xl mx-auto px-4 py-16'} data-id="element-2018" data-mp="1sulix4i2wt7j">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[#D4AF37]/20 pb-4 mb-8" data-id="element-2019" data-mp="cglo27y3btr5">
        <div data-id="element-2020" data-mp="hfoo1dz7r9s9">
          <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.35em] uppercase mb-1.5" data-id="element-2021" data-mp="hg7zcbucyzxb">
            {created ? 'Your Misl' : 'Found Your House'}
          </p>
          <h3 className="font-serif text-2xl sm:text-3xl text-[#FDFBF7] leading-tight" data-id="element-2022" data-mp="9ya2p7x9zjbi">
            {created ? name : 'Create Your Own Misl'}
          </h3>
        </div>
        {embedded && onClose && <button onClick={onClose} className="flex items-center gap-2 text-[#D4AF37] hover:text-[#E5C158] text-[10px] uppercase tracking-widest border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10 px-3 py-2 transition-colors" data-id="element-2023" data-mp="iuhnunklha1k">
            <ChevronLeft className="w-3 h-3" data-id="element-2024" data-mp="3ida869lm7j4m" />
            Back
          </button>}
      </div>

      {!created /* ============ STAGE 1: EXCITING + INSTRUCTIVE CREATE ============ */ ? <div className="space-y-8" data-id="element-2025" data-mp="3rbvpk1fcos3t">
          {/* Inspiring banner */}
          <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="relative border border-[#D4AF37]/30 bg-gradient-to-br from-[#1A1410] via-[#131B2B] to-[#0B0F19] p-6 sm:p-7 overflow-hidden" data-id="element-2026" data-mp="33mku8zgrrpx2">
            <CornerOrnaments data-id="element-2027" data-mp="1cbpsqzury9pb" />
            <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" data-id="element-2028" data-mp="1cbg8zu0boys2" />
            <div className="absolute -right-16 -top-16 w-56 h-56 bg-[#D4AF37]/10 blur-[100px] rounded-full pointer-events-none" data-id="element-2029" data-mp="jm91w6msz0v7" />
            <div className="absolute -left-16 -bottom-16 w-56 h-56 bg-[#8B0000]/10 blur-[100px] rounded-full pointer-events-none" data-id="element-2030" data-mp="om57ubf292de" />

            <div className="relative z-10 flex items-start gap-5" data-id="element-2031" data-mp="tivmqvf4prx8">
              <motion.div animate={{
            rotate: [0, -4, 4, -4, 0]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            repeatDelay: 2
          }} className="shrink-0 w-14 h-14 border-2 border-[#D4AF37]/60 bg-[#D4AF37]/10 flex items-center justify-center" data-id="element-2032" data-mp="xmtjx0kw8snn">
                <Crown className="w-7 h-7 text-[#D4AF37]" strokeWidth={1.5} data-id="element-2033" data-mp="3pinzwvsel389" />
              </motion.div>
              <div className="flex-1" data-id="element-2034" data-mp="3kp3b9ucp9llq">
                <p className="text-[#D4AF37] text-[10px] tracking-[0.3em] uppercase font-bold mb-2 flex items-center gap-2" data-id="element-2035" data-mp="3frl0rzj1aybm">
                  <Sparkles className="w-3 h-3" data-id="element-2036" data-mp="3at01c2blwio8" />
                  Forge Your Legacy
                </p>
                <h4 className="font-serif text-xl sm:text-2xl text-[#FDFBF7] leading-tight mb-2" data-id="element-2037" data-mp="35t44cn1btri6">
                  Lead a Clan in the Lahore Court
                </h4>
                <p className="text-[#FDFBF7]/60 text-sm leading-relaxed" data-id="element-2038" data-mp="2wsp8itqqku0f">
                  A Misl is your house — your name, your people, your standing.
                  Found yours, gather your sangat, and rise together.
                </p>
              </div>
            </div>

            {/* Perks chips */}
            <div className="relative z-10 mt-5 flex flex-wrap gap-2" data-id="element-2039" data-mp="3w8bdv3kqsjk">
              {perks.map(p => <span key={p.label} className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-[#D4AF37]/25 bg-[#0B0F19]/60 text-[#FDFBF7]/70 text-[10px] tracking-[0.2em] uppercase" data-id="element-2040" data-mp="8vkzej66m5nq">
                  <p.icon className="w-3 h-3 text-[#D4AF37]" data-id="element-2041" data-mp="3v8tdiq0x6v7u" />
                  {p.label}
                </span>)}
            </div>
          </motion.div>

          {/* Visible 3-step instructions */}
          <div data-id="element-2042" data-mp="3la560l6k9yie">
            <div className="flex items-center gap-3 mb-4" data-id="element-2043" data-mp="37cyp6w5ee9vt">
              <span className="font-mono text-[#D4AF37] text-[10px] tracking-[0.3em] uppercase" data-id="element-2044" data-mp="37ejjqlrppsto">
                How It Works
              </span>
              <span className="flex-1 h-px bg-[#D4AF37]/15" data-id="element-2045" data-mp="15llokjagqo5k" />
              <span className="text-[#FDFBF7]/40 text-[10px] tracking-[0.25em] uppercase" data-id="element-2046" data-mp="15j95u3wvs9rg">
                3 Steps
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-2047" data-mp="2v1o9ryqbhgp6">
              {steps.map((s, i) => <motion.div key={s.n} initial={{
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4,
            delay: 0.1 + i * 0.1
          }} className="relative border border-[#D4AF37]/15 bg-[#0B0F19] p-4 hover:border-[#D4AF37]/40 transition-colors" data-id="element-2048" data-mp="2q4fm5imegkgj" data-mpi={i}>
                  <div className="flex items-center justify-between mb-3" data-id="element-2049" data-mp="3nosfy715c12j">
                    <div className="w-9 h-9 border border-[#D4AF37]/40 bg-[#131B2B] flex items-center justify-center" data-id="element-2050" data-mp="3istlq2t4zou7">
                      <s.icon className="w-4 h-4 text-[#D4AF37]" data-id="element-2051" data-mp="1teykrfug9kkn" />
                    </div>
                    <span className="font-mono text-[#D4AF37]/40 text-[10px] tracking-[0.25em]" data-id="element-2052" data-mp="1og3ujsu5ktqi">
                      {s.n}
                    </span>
                  </div>
                  <h5 className="font-serif text-sm text-[#FDFBF7] leading-tight mb-1.5" data-id="element-2053" data-mp="1g1ypxda5bus6">
                    {s.title}
                  </h5>
                  <p className="text-[#FDFBF7]/50 text-[11px] leading-relaxed" data-id="element-2054" data-mp="165dlou5yanpe">
                    {s.body}
                  </p>
                </motion.div>)}
            </div>
          </div>

          {/* Create form */}
          <motion.div initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        delay: 0.4
      }} className="border border-[#D4AF37]/25 bg-[#0B0F19] p-6 sm:p-7 relative" data-id="element-2055" data-mp="1yv2nvauectsl">
            <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/60 to-transparent" data-id="element-2056" data-mp="2hsnbyfpc7oc5" />
            <p className="text-[#D4AF37] text-[10px] tracking-[0.3em] uppercase font-bold mb-5 flex items-center gap-2" data-id="element-2057" data-mp="2q346ksm9viu4">
              <span className="w-1 h-1 bg-[#D4AF37] rounded-full" data-id="element-2058" data-mp="2u57lxsc2b7ko" />
              Step 01 · Name Your Misl
            </p>
            <div className="space-y-5" data-id="element-2059" data-mp="34115hvo781by">
              <div data-id="element-2060" data-mp="3423svtmbbln4">
                <label className="block text-[#FDFBF7]/70 text-[10px] tracking-[0.3em] uppercase mb-2" data-id="element-2061" data-mp="342m0fmefdmac">
                  Misl Name
                </label>
                <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="The Singh House" autoFocus className="w-full bg-[#131B2B] border border-[#D4AF37]/25 px-4 py-3.5 text-[#FDFBF7] text-base placeholder:text-[#FDFBF7]/25 focus:outline-none focus:border-[#D4AF37]/60 transition-colors" data-id="element-2062" data-mp="3d17fh7kouj6d" />
                <p className="mt-2 text-[#FDFBF7]/35 text-[10px] leading-relaxed" data-id="element-2063" data-mp="ku4jwx64i6v">
                  Tip — strong Misl names are short, distinct, and feel like
                  yours.
                </p>
              </div>
              <div data-id="element-2064" data-mp="3grnln1tvycad">
                <label className="block text-[#FDFBF7]/70 text-[10px] tracking-[0.3em] uppercase mb-2" data-id="element-2065" data-mp="3gs5rmq6co4vh">
                  City{' '}
                  <span className="text-[#FDFBF7]/30 normal-case tracking-normal" data-id="element-2066" data-mp="3bzckjvneskg8">
                    (optional)
                  </span>
                </label>
                <div className="relative" data-id="element-2067" data-mp="3ktqx3qgk1gvn">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D4AF37]/40" data-id="element-2068" data-mp="3ksportqxb8hm" />
                  <input type="text" value={region} onChange={e => setRegion(e.target.value)} placeholder="Toronto" className="w-full bg-[#131B2B] border border-[#D4AF37]/25 pl-11 pr-4 py-3.5 text-[#FDFBF7] text-base placeholder:text-[#FDFBF7]/25 focus:outline-none focus:border-[#D4AF37]/60 transition-colors" data-id="element-2069" data-mp="3kcbyl3rw4iw" />
                </div>
              </div>
              <button onClick={handleCreate} disabled={!name.trim()} className={`group w-full py-4 text-[10px] tracking-[0.3em] uppercase font-bold flex items-center justify-center gap-2.5 transition-all ${name.trim() ? 'bg-[#D4AF37] text-[#0B0F19] hover:bg-[#E5C158] shadow-[0_0_40px_rgba(212,175,55,0.35)]' : 'bg-[#D4AF37]/20 text-[#0B0F19]/40 cursor-not-allowed'}`} data-id="element-2070" data-mp="kt4v8ui0kmqo">
                <Crown className="w-4 h-4" data-id="element-2071" data-mp="14cmb72k3yeq" />
                Found My Misl
                <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" data-id="element-2072" data-mp="3jdmw8ot44myx" />
              </button>
              <p className="text-[#FDFBF7]/40 text-[10px] text-center leading-relaxed" data-id="element-2073" data-mp="3od9b12olab9u">
                Free · You can rename, add a city, or change your crest later
              </p>
            </div>
          </motion.div>
        </div> /* ============ STAGE 2: FORMING MISL ============ */ : <div className="space-y-6 relative" data-id="element-2074" data-mp="3ikkf785a9sf8">
          {/* Just-created celebration overlay */}
          <AnimatePresence data-id="element-2075" data-mp="3dlr39k5na9h6">
            {justCreated && <motion.div initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} className="absolute -top-2 left-0 right-0 z-20 flex justify-center pointer-events-none" data-id="element-2076" data-mp="39kxc8hwwoek2">
                <div className="flex items-center gap-2 px-4 py-2.5 bg-[#D4AF37] text-[#0B0F19] text-[10px] tracking-[0.3em] uppercase font-bold shadow-[0_0_30px_rgba(212,175,55,0.5)]" data-id="element-2077" data-mp="2pvwrz70z4sz1">
                  <PartyPopper className="w-3.5 h-3.5" data-id="element-2078" data-mp="2gznmbameky4g" />
                  Misl Founded — Now Bring Your People
                </div>
              </motion.div>}
          </AnimatePresence>

          {/* Status hero */}
          <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="border border-[#D4AF37]/30 bg-gradient-to-br from-[#1A1410] via-[#131B2B] to-[#0B0F19] p-6 relative overflow-hidden" data-id="element-2079" data-mp="wfa9mo0jqwta">
            <CornerOrnaments data-id="element-2080" data-mp="do9b213c93a9" />
            <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" data-id="element-2081" data-mp="dpkbbqxkrswm" />
            <div className="absolute -right-20 -top-20 w-56 h-56 bg-[#D4AF37]/10 blur-[100px] rounded-full pointer-events-none" data-id="element-2082" data-mp="hot9d7znecxz" />
            <div className="relative z-10 flex items-start justify-between gap-4 mb-5 flex-wrap" data-id="element-2083" data-mp="3m5drg4nzv2d5">
              <div data-id="element-2084" data-mp="3q5xt9ljiiu5m">
                <div className="flex items-center gap-2 mb-2" data-id="element-2085" data-mp="3q54sr2b5d0vx">
                  <span className={`w-2 h-2 rounded-full ${isActive ? 'bg-[#D4AF37]' : 'bg-[#FDFBF7]/50 animate-pulse'} shadow-[0_0_10px_currentColor]`} data-id="element-2086" data-mp="xbpgeafbfkk4" />
                  <span className={`text-[9px] tracking-[0.3em] uppercase font-bold ${isActive ? 'text-[#D4AF37]' : 'text-[#FDFBF7]/70'}`} data-id="element-2087" data-mp="171iqt83q1tms">
                    {isActive ? 'Active Misl' : 'Forming Misl'}
                  </span>
                </div>
                <p className="font-serif text-4xl text-[#FDFBF7] leading-tight" data-id="element-2088" data-mp="9e07528iv740">
                  {memberCount}{' '}
                  <span className="text-[#FDFBF7]/30" data-id="element-2089" data-mp="neaz68lkirqr">/ {target}</span>
                </p>
                <p className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase mt-1 font-medium" data-id="element-2090" data-mp="jcfz17fdikek">
                  {isActive ? '🔥 Your house is live' : `${target - memberCount} more to activate`}
                </p>
              </div>
              {region && <div className="flex items-center gap-1.5 text-[#FDFBF7]/40 text-[10px] tracking-[0.25em] uppercase" data-id="element-2091" data-mp="22b5lm90b4sxg">
                  <MapPin className="w-3 h-3" data-id="element-2092" data-mp="27a8mrid62f05" />
                  {region}
                </div>}
            </div>
            <div className="relative z-10 h-2 bg-[#D4AF37]/10 overflow-hidden" data-id="element-2093" data-mp="1se9arwmxrupq">
              <motion.div animate={{
            width: `${progress}%`
          }} transition={{
            duration: 0.6,
            ease: 'easeOut'
          }} className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#D4AF37]/60 via-[#D4AF37] to-[#E5C158] shadow-[0_0_15px_rgba(212,175,55,0.4)]" data-id="element-2094" data-mp="1xckhhyhxpp3q" />
            </div>
          </motion.div>

          {/* Next-step nudge */}
          {!isActive && <div className="border border-[#D4AF37]/25 bg-[#D4AF37]/[0.04] px-4 py-3 flex items-center gap-3" data-id="element-2095" data-mp="33zg1o7ja5pv3">
              <Zap className="w-4 h-4 text-[#D4AF37] shrink-0" data-id="element-2096" data-mp="38siv8fo24zba" />
              <p className="text-[#FDFBF7]/75 text-xs leading-relaxed" data-id="element-2097" data-mp="18oyhutbl9r1r">
                <span className="text-[#D4AF37] font-bold" data-id="element-2098" data-mp="13y015smouqxz">Next step:</span>{' '}
                Send your invite link to {target - memberCount} friends — anyone
                you bring in can also invite more.
              </p>
            </div>}

          {/* Big invite block */}
          <div className="border border-[#D4AF37]/30 bg-gradient-to-br from-[#131B2B] to-[#0B0F19] p-6 relative" data-id="element-2099" data-mp="1rnkjc9b0pk67">
            <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" data-id="element-2100" data-mp="1nm8u44w230yj" />
            <div className="flex items-center gap-3 mb-4" data-id="element-2101" data-mp="1ill2bh006g39">
              <UserPlus className="w-4 h-4 text-[#D4AF37]" data-id="element-2102" data-mp="1do1o2h3b23bb" />
              <p className="font-serif text-base text-[#FDFBF7]" data-id="element-2103" data-mp="1dnjl71m50quf">
                Invite Your People
              </p>
            </div>
            <div className="flex items-center gap-2 border border-[#D4AF37]/20 bg-[#0B0F19] px-3 py-2.5 mb-3" data-id="element-2104" data-mp="28w7qaa94698">
              <span className="font-mono text-[#D4AF37]/80 text-xs truncate flex-1" data-id="element-2105" data-mp="3oalpnx88n9pq">
                {inviteLink}
              </span>
              <button onClick={handleCopy} className="shrink-0 flex items-center gap-1.5 px-2.5 py-1 border border-[#D4AF37]/30 text-[#D4AF37] text-[9px] tracking-[0.2em] uppercase hover:bg-[#D4AF37]/10 transition-colors" data-id="element-2106" data-mp="3t9olcr2bw3l1">
                {copied ? <>
                    <Check className="w-2.5 h-2.5" data-id="element-2107" data-mp="ax9xhk8i94wz" />
                    Copied
                  </> : <>
                    <Copy className="w-2.5 h-2.5" data-id="element-2108" data-mp="kt3jrvc10cry" />
                    Copy
                  </>}
              </button>
            </div>
            <button onClick={handleWhatsApp} className="w-full flex items-center justify-center gap-2.5 px-6 py-3.5 bg-[#25D366] text-white hover:bg-[#128C7E] transition-colors text-[10px] tracking-[0.25em] uppercase font-bold" data-id="element-2109" data-mp="pqdg7uxu1opa">
              <MessageCircle className="w-4 h-4" data-id="element-2110" data-mp="zjrydasts2r4" />
              Share on WhatsApp
            </button>
            <p className="text-[#FDFBF7]/40 text-[10px] text-center mt-3 leading-relaxed" data-id="element-2111" data-mp="13klomatqptn6">
              Every member can invite. Build your house together.
            </p>
          </div>

          {/* Member list */}
          <div className="border border-[#D4AF37]/20 bg-[#0B0F19]" data-id="element-2112" data-mp="1dbygv3jgaltq">
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#D4AF37]/15" data-id="element-2113" data-mp="1i86qk1evpmti">
              <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.3em] uppercase" data-id="element-2114" data-mp="1n6qc40pk8wlc">
                Members
              </p>
              <span className="font-mono text-[#D4AF37] text-xs" data-id="element-2115" data-mp="1r7k00u306be8">
                {memberCount} / {target}
              </span>
            </div>
            <div className="divide-y divide-[#D4AF37]/10" data-id="element-2116" data-mp="3t1t4td0dpi1d">
              {members.map(m => <motion.div key={m.id} initial={{
            opacity: 0,
            y: -8
          }} animate={{
            opacity: 1,
            y: 0
          }} className="flex items-center gap-3 px-5 py-3 hover:bg-[#131B2B]/40 transition-colors" data-id="element-2117" data-mp="1tx2l8eju037">
                  <div className={`w-9 h-9 shrink-0 rounded-full border ${m.role === 'founder' ? 'border-[#D4AF37] bg-[#D4AF37]/15' : 'border-[#D4AF37]/30 bg-[#131B2B]'} flex items-center justify-center`} data-id="element-2118" data-mp="3bs79po8bnk4l">
                    {m.role === 'founder' ? <Crown className="w-4 h-4 text-[#D4AF37]" data-id="element-2119" data-mp="3bci54wz5m5t" /> : <User className="w-4 h-4 text-[#FDFBF7]/50" data-id="element-2120" data-mp="3vfy7z8aodnzc" />}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-2121" data-mp="2o2d0rhr4ifpm">
                    <p className="font-serif text-sm text-[#FDFBF7] leading-tight truncate" data-id="element-2122" data-mp="2o007w9jtukxs">
                      {m.name}
                    </p>
                    <p className="font-mono text-[#FDFBF7]/40 text-[10px] truncate" data-id="element-2123" data-mp="2lhf01vx6elh7">
                      {m.handle} · {m.joinedAt}
                    </p>
                  </div>
                  {m.role === 'founder' && <span className="shrink-0 text-[#D4AF37] text-[9px] tracking-[0.25em] uppercase border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-2 py-0.5" data-id="element-2124" data-mp="2biz8jru8la76">
                      Founder
                    </span>}
                  <button onClick={handleWhatsApp} className="shrink-0 text-[#D4AF37]/60 hover:text-[#D4AF37] text-[9px] tracking-[0.25em] uppercase border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 px-2 py-1 transition-colors flex items-center gap-1" data-id="element-2125" data-mp="22pcec65ogf03">
                    <Share2 className="w-2.5 h-2.5" data-id="element-2126" data-mp="1jsl04b4zsq2w" />
                    Invite
                  </button>
                </motion.div>)}
              {Array.from({
            length: Math.max(0, target - memberCount)
          }).map((_, i) => <div key={`empty-${i}`} className="flex items-center gap-3 px-5 py-3 opacity-60" data-id="element-2127" data-mp="157qe52jon2qk" data-mpi={i}>
                  <div className="w-9 h-9 shrink-0 rounded-full border border-dashed border-[#D4AF37]/20 bg-[#0B0F19] flex items-center justify-center" data-id="element-2128" data-mp="1e6ken404wd9e">
                    <Plus className="w-3 h-3 text-[#D4AF37]/30" data-id="element-2129" data-mp="199kbp1ykzqmi" />
                  </div>
                  <p className="text-[#FDFBF7]/30 text-xs italic flex-1" data-id="element-2130" data-mp="1o2dr6ndus6r6">
                    Empty seat — invite someone
                  </p>
                  {i === 0 && memberCount < target && <button onClick={handleSimulateJoin} disabled={simulating} className="shrink-0 text-[#FDFBF7]/30 hover:text-[#D4AF37]/70 text-[9px] tracking-[0.25em] uppercase transition-colors" data-id="element-2131" data-mp="1wywffmb536hf">
                      {simulating ? '...' : 'Simulate join'}
                    </button>}
                </div>)}
            </div>
          </div>

          {/* Active reward unlock teaser */}
          {!isActive && <div className="border border-[#D4AF37]/15 bg-[#0B0F19] p-4 flex items-center gap-4" data-id="element-2132" data-mp="2upt7btfxab05">
              <Trophy className="w-5 h-5 text-[#D4AF37]/70 shrink-0" data-id="element-2133" data-mp="2zpxlvdx9svnq" />
              <div className="flex-1" data-id="element-2134" data-mp="2zr8txpxr0pkn">
                <p className="text-[#FDFBF7] text-sm leading-tight" data-id="element-2135" data-mp="2306tbk8jhikk">
                  Unlock at{' '}
                  <span className="text-[#D4AF37] font-bold" data-id="element-2136" data-mp="271rzynuhd0mb">5 members</span>
                </p>
                <p className="text-[#FDFBF7]/45 text-[11px] leading-relaxed mt-0.5" data-id="element-2137" data-mp="2bys8d5esyfgl">
                  Misl Leaderboard · Group XP · Founder Crown · Court rank
                </p>
              </div>
              <span className="font-mono text-[#D4AF37] text-sm" data-id="element-2138" data-mp="3ndxcmdul0m2b">
                {target - memberCount}{' '}
                <span className="text-[#FDFBF7]/40 text-[10px] uppercase tracking-widest" data-id="element-2139" data-mp="3scs4s6efvmz3">
                  to go
                </span>
              </span>
            </div>}
        </div>}
    </div>;
};